import { useEffect, useMemo } from "react";
import { favoriteFiltersListName } from "src/api/config";
import { useFiltersContext } from "src/contexts/FilterContext";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import useDocDefaultFilters from "src/hooks/useDocDefaultFilters";
import { getItems, updateItem } from "src/modules/spItems/actions";
import {
  selectItems,
  selectJustAddedItem,
  selectJustDeletedItem,
  selectUpdateStatus,
} from "src/modules/spItems/selectors";
import { selectCurrentUser } from "src/modules/users/selector";
import { FiltersStateType } from "src/pages/DashboardPage/acadia/type";
import { isDocFiltersEmpty } from "src/pages/DashboardPage/acadia/utils/docFilters.utils";
import DocBundlesModal from "./DocBundlesModal";

export { isDocFiltersEmpty };

function areDocFiltersEqual(f1: FiltersStateType, f2: FiltersStateType): boolean {
  const checkboxKeys: (keyof FiltersStateType)[] = [
    "collections",
    "departments",
    "sectors",
    "processes",
    "types",
    "critical",
    "lines",
    "sap",
    "typeEquipment",
    "model",
    "manufacturer",
    "titleEquipment",
    "workstations",
    "templateOWD",
    "notificationStatus",
    "notificationType",
  ];

  const getIds = (arr: { id: number | string }[]): (number | string)[] =>
    arr ? arr.map((item) => item.id).sort() : [];

  const getEmails = (arr: { email?: string }[]): string[] =>
    arr ? arr.map((item) => item.email?.toLowerCase() ?? "").sort() : [];

  for (const key of checkboxKeys) {
    const ids1 = getIds(f1[key] as { id: number }[]);
    const ids2 = getIds(f2[key] as { id: number }[]);
    if (JSON.stringify(ids1) !== JSON.stringify(ids2)) return false;
  }

  if (JSON.stringify(getEmails(f1.responsibleForRevision)) !== JSON.stringify(getEmails(f2.responsibleForRevision)))
    return false;
  if (JSON.stringify(getEmails(f1.created)) !== JSON.stringify(getEmails(f2.created))) return false;

  const d1 = f1.dateForRevision;
  const d2 = f2.dateForRevision;
  if ((d1?.required ?? false) !== (d2?.required ?? false)) return false;
  if (String(d1?.date?.start ?? null) !== String(d2?.date?.start ?? null)) return false;
  if (String(d1?.date?.end ?? null) !== String(d2?.date?.end ?? null)) return false;

  return true;
}

type Props = {
  isFavorite: boolean;
  setIsFavorite: (v: boolean) => void;
  currentFavoriteId: string | null;
  setCurrentFavoriteId: (id: string | null) => void;
  bundlesModalOpened: boolean;
  setBundlesModalOpened: (v: boolean) => void;
  setFiltersReady: (v: boolean) => void;
};

function DocFavoriteFiltersManager({
  isFavorite,
  setIsFavorite,
  currentFavoriteId,
  setCurrentFavoriteId,
  bundlesModalOpened,
  setBundlesModalOpened,
  setFiltersReady,
}: Props) {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => selectCurrentUser(state));
  const favoriteFiltersData = useAppSelector((state) => selectItems(state, favoriteFiltersListName));
  const favoriteFiltersUpdatedStatus = useAppSelector((state) => selectUpdateStatus(state, favoriteFiltersListName));
  const favoriteFiltersAdded = useAppSelector((state) => selectJustAddedItem(state, favoriteFiltersListName));
  const favoriteFiltersDeleted = useAppSelector((state) => selectJustDeletedItem(state, favoriteFiltersListName));

  const { filters, updateFilter } = useFiltersContext();

  const filtersKey = useMemo(() => {
    const keys: (keyof FiltersStateType)[] = ["collections", "departments", "sectors", "processes", "types"];
    return JSON.stringify(keys.map((k) => (filters[k] as { id: number }[]).map((i) => i.id)));
  }, [JSON.stringify(filters)]);

  // Auto-apply priority chain (Priority 1 → 2 → 3) — delegated to reusable hook
  useDocDefaultFilters({
    onReady: setFiltersReady,
    onFavoriteApplied: (filterId) => {
      setCurrentFavoriteId(filterId);
      setIsFavorite(true);
    },
  });

  // Keep isFavorite in sync with currently applied filters
  useEffect(() => {
    if (!favoriteFiltersData.length) {
      setCurrentFavoriteId(null);
      setIsFavorite(false);
      return;
    }

    const matched = favoriteFiltersData.find((item) => {
      try {
        const parsed = JSON.parse(item.DocumentsFilter) as FiltersStateType;
        return areDocFiltersEqual(parsed, filters);
      } catch {
        return false;
      }
    });

    if (matched) {
      const wasJustDeleted = favoriteFiltersDeleted && matched.ID.toString() === favoriteFiltersDeleted.toString();
      if (!wasJustDeleted) {
        setCurrentFavoriteId(matched.ID.toString());
        setIsFavorite(true);
      } else {
        setCurrentFavoriteId(null);
        setIsFavorite(false);
      }
    } else {
      setCurrentFavoriteId(null);
      setIsFavorite(false);
    }
  }, [favoriteFiltersUpdatedStatus, favoriteFiltersData, favoriteFiltersAdded, favoriteFiltersDeleted, filtersKey]);

  // Reload list after a new favourite is added
  useEffect(() => {
    if (favoriteFiltersAdded && user.id) {
      setCurrentFavoriteId(favoriteFiltersAdded.toString());
      setIsFavorite(true);
      dispatch(getItems({ listName: favoriteFiltersListName, filters: { Author: user.id, Type: "doc" } }));
    }
  }, [favoriteFiltersAdded, user.id]);

  // Reset state when the current favourite is deleted
  useEffect(() => {
    if (favoriteFiltersDeleted) {
      if (currentFavoriteId === favoriteFiltersDeleted.toString()) {
        setCurrentFavoriteId(null);
        setIsFavorite(false);
      }
      setBundlesModalOpened(false);
    }
  }, [favoriteFiltersDeleted]);

  const applyFavoriteFilter = (filterId: string) => {
    const filter = favoriteFiltersData.find((item) => item.ID.toString() === filterId);
    if (filter) {
      const parsed = JSON.parse(filter.DocumentsFilter) as FiltersStateType;
      updateFilter(parsed);
      setCurrentFavoriteId(filterId);
      setIsFavorite(true);
      setBundlesModalOpened(false);
    }
  };

  const updateDefaultStatus = (id: string, checked: boolean) => {
    const clicked = favoriteFiltersData.find((item) => item.ID.toString() === id);
    if (!clicked) return;

    if (clicked.isDefault) {
      dispatch(updateItem({ listName: favoriteFiltersListName, body: { ID: clicked.ID, isDefault: false } }));
    } else {
      favoriteFiltersData
        .filter((item) => item.isDefault && item.ID.toString() !== id)
        .forEach((item) => {
          dispatch(updateItem({ listName: favoriteFiltersListName, body: { ID: item.ID, isDefault: false } }));
        });
      dispatch(updateItem({ listName: favoriteFiltersListName, body: { ID: clicked.ID, isDefault: true } }));
    }
  };

  if (bundlesModalOpened) {
    return (
      <DocBundlesModal
        opened={bundlesModalOpened}
        onApplyFilter={applyFavoriteFilter}
        currentFavoriteId={currentFavoriteId}
        onUpdateDefault={updateDefaultStatus}
      />
    );
  }

  return null;
}

export default DocFavoriteFiltersManager;
